package com.bikompanion.data.parking

import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.minutes
import kotlin.time.TimeMark
import kotlin.time.TimeSource

/** Consecutive failures before an instance is left alone for a while. */
private const val FailuresBeforeTripping = 2

/** How long a tripped instance is skipped for. Long enough to outlast a throttling window. */
private val EndpointCooldown = 5.minutes

/**
 * Which Overpass instances are worth asking right now.
 *
 * Overpass refuses an address it has decided to throttle for minutes at a time, and it refuses
 * by dropping the connection rather than by answering. Without this, every camera move pays the
 * full connect timeout against a host that has already said no twice — which is both the slowest
 * possible way to fail and the thing that keeps the throttle alive.
 *
 * In memory and for this session only: the point is to stop repeating a mistake within one sitting,
 * not to remember a mirror's bad afternoon into next week.
 *
 * The lock guards the map and nothing else — see the deadlock recorded in `GbfsApi`, and note that
 * no request is ever made while holding it.
 */
internal class EndpointHealth(
    private val timeSource: TimeSource = TimeSource.Monotonic,
    private val cooldown: Duration = EndpointCooldown,
    private val failuresBeforeTripping: Int = FailuresBeforeTripping,
) {
    private val lock = Mutex()
    private val trouble = mutableMapOf<String, Trouble>()

    /** [endpoints] worth asking, in the order given; empty only if every one is in cooldown. */
    suspend fun usable(endpoints: List<String>): List<String> =
        lock.withLock {
            endpoints.filter { endpoint ->
                val entry = trouble[endpoint] ?: return@filter true
                !entry.isTripped(failuresBeforeTripping, cooldown)
            }
        }

    suspend fun recordSuccess(endpoint: String) =
        lock.withLock {
            // A single answer clears the record outright rather than decrementing: an instance that
            // is serving again is serving, and holding a grudge would trip it on the next blip.
            trouble.remove(endpoint)
            Unit
        }

    suspend fun recordFailure(endpoint: String) =
        lock.withLock {
            val previous = trouble[endpoint]
            val consecutive = if (previous == null || previous.hasCooledOff(cooldown)) 1 else previous.consecutive + 1
            trouble[endpoint] = Trouble(consecutive = consecutive, at = timeSource.markNow())
        }

    private class Trouble(
        val consecutive: Int,
        val at: TimeMark,
    ) {
        fun hasCooledOff(cooldown: Duration) = at.elapsedNow() >= cooldown

        fun isTripped(
            threshold: Int,
            cooldown: Duration,
        ) = consecutive >= threshold && !hasCooledOff(cooldown)
    }
}
